<template>
  <b-form-group :label="label" :label-for="id">
    <div :id="id" class="image-selector rounded p-2" @click="openFileInput">
      <!-- Image Preview Grid -->
      <div class="d-flex flex-wrap justify-content-center gap-2 w-100">
        <!-- Existing Images -->
        <div
          v-for="(imageUrl, index) in images"
          :key="`image-${index}`"
          class="position-relative rounded overflow-hidden"
          style="width: 120px; height: 120px"
        >
          <b-button
            variant="danger"
            @click.stop="removeImage(index)"
            style="position: absolute; top: 0px; right: 0; z-index: 1000"
            size="sm"
            class="btn-icon"
          >
            <feather-icon icon="TrashIcon" />
          </b-button>

          <b-overlay
            :show="loadingStates[index] || false"
            rounded="sm"
            opacity="0.5"
            spinner-variant="primary"
            class="w-100 h-100 rounded overflow-hidden"
          >
            <img :src="imageUrl" alt="Image" class="w-100 h-100" style="object-fit: cover" />
          </b-overlay>
        </div>

        <!-- Add New Image Button -->
        <div class="add-image-button rounded d-flex align-items-center justify-content-center">
          <feather-icon icon="PlusIcon" class="text-muted" size="2x" />
        </div>
      </div>

      <b-form-file
        v-model="imageFile"
        :placeholder="placeholder"
        :state="state"
        ref="fileInput"
        accept="image/*"
        class="w-100"
        @change="handleFileChange"
        style="display: none"
        multiple
      />
    </div>
  </b-form-group>
</template>

<script>
import { APIService } from '@/utils/api';
import { BButton, BFormFile, BFormGroup, BOverlay } from 'bootstrap-vue';

export default {
  name: 'MultiImageField',
  components: {
    BFormFile,
    BButton,
    BFormGroup,
    BOverlay,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      required: true,
    },
    state: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingStates: {},
      imageFile: null,
      images: Array.isArray(this.value) ? [...this.value] : [],
    };
  },
  mounted() {
    console.log('[MultiImageField] Mounted with value:', this.value);
    // Initialize with existing images from value prop
    this.initializeImages();
  },
  watch: {
    value: {
      handler(newVal) {
        console.log('[MultiImageField] Value changed:', newVal);
        this.initializeImages();
      },
      deep: true,
    },
  },
  methods: {
    initializeImages() {
      console.log('=== [MultiImageField] initializeImages ===', typeof this.value, this.value);
      if (!this.value) return;

      // Handle array values
      if (
        typeof this.value === 'string' &&
        this.value.trim().length > 0 &&
        this.value.includes(',')
      ) {
        console.log('[MultiImageField] value split trim:');
        this.images = this.value.split(',').filter((url) => url.trim().length > 0);
      } else if (Array.isArray(this.value)) {
        console.log('[MultiImageField] value is an array:');
        this.images = this.value.filter((url) => url.trim().length > 0);
      } else if (typeof this.value === 'string' && this.value.trim().length > 0) {
        console.log('[MultiImageField] value is a single string:');
        this.images = [this.value];
      }
      // Default to empty array
      else {
        console.log('[MultiImageField] value is empty:');
        this.images = [];
      }
      console.log('[MultiImageField] Initialized images:', this.images);
      console.log('===============================================================');
    },
    removeImage(index) {
      // Create a copy of the images array
      const updatedImages = [...this.images];
      // Remove the image at the specified index
      updatedImages.splice(index, 1);
      // Update local array
      this.images = updatedImages;
      // Emit the updated array
      this.$emit('input', updatedImages);
    },
    openFileInput() {
      if (this.$refs.fileInput) {
        const nativeInput = this.$refs.fileInput.$el.querySelector('input[type="file"]');
        if (nativeInput) {
          nativeInput.click();
        }
      }
    },
    handleFileChange(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Process each file
      Array.from(files).forEach((file) => {
        // Add a placeholder for the new file
        const newIndex = this.images.length;
        const tempUrl = URL.createObjectURL(file);

        // Add the temporary URL to the images array
        const updatedImages = [...this.images, tempUrl];
        this.images = updatedImages;

        // Set loading state for this specific file
        this.$set(this.loadingStates, newIndex, true);

        // Use either store dispatch or APIService, depending on what's available
        const uploadMethod = APIService.uploadFile(file);

        // Upload the file
        Promise.resolve(uploadMethod)
          .then((url) => {
            // Update the temporary URL with the actual URL
            const finalImages = [...this.images];
            finalImages[newIndex] = url;
            this.images = finalImages;

            console.log('[MultiImageField] Uploaded images:', finalImages);

            // Emit the updated array of URLs
            this.$emit('input', finalImages);
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
            // Remove the failed upload
            this.removeImage(newIndex);
          })
          .finally(() => {
            // Clear loading state
            this.$set(this.loadingStates, newIndex, false);
          });
      });

      // Reset file input
      this.imageFile = null;
    },
    removeFile(index) {
      this.removeImage(index);
    },
  },
};
</script>

<style lang="scss" scoped>
.gap-2 {
  gap: 0.5rem;
}

.image-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-color: var(--secondary);
  border-width: 2px !important;
  border-style: dashed !important;

  cursor: pointer;

  &:hover {
    border-color: var(--primary);
    .add-image-button {
      background-color: var(--primary);
      opacity: 0.75;
    }
  }

  .add-image-button {
    background-color: var(--secondary);
    opacity: 0.5;
    width: 120px;
    height: 120px;
    position: relative;
  }
}
</style>
